import type { StructuredAddress } from '@api/hooks/types/createTransaction.ts';
import apiClient from '@services/apiClient.ts';
import type { Address } from 'types/operations.ts';

// Finite `gcTime` so unobserved cache entries are collected after the form
// unmounts instead of living for the whole session.
export const VALIDATION_GC_TIME = 10 * 60 * 1000; // 10 minutes

interface BicDetailsApiResponse {
	valid: boolean;
	details?: {
		name?: string;
		address?: Partial<Address> & { building_number?: string | null };
	};
}

export interface BicValidationResult {
	isValid: boolean;
	name?: string;
	address?: StructuredAddress;
}

const mapApiResponse = (data: BicDetailsApiResponse): BicValidationResult => {
	const address = data.details?.address;
	return {
		isValid: data.valid,
		name: data.details?.name,
		address: address
			? {
					type: 'STRUCTURED',
					country: address.country ?? '',
					line: address.line ?? '',
					town: address.town ?? '',
					building_number: address.building_number ?? '',
					building_name: address.building_name ?? '',
					street_name: address.street_name ?? '',
					country_sub_division: address.country_sub_division ?? '',
					postal_code: address.postal_code ?? '',
				}
			: undefined,
	};
};

export const validateBicApi = async (bic: string): Promise<BicValidationResult> => {
	if (!bic) {
		return { isValid: true };
	}

	try {
		const response = await apiClient.post<BicDetailsApiResponse>(
			'/utilities/bics/details',
			{ bic, includeDetails: true },
		);

		return response.data ? mapApiResponse(response.data) : { isValid: true };
	} catch {
		console.error('BIC validation API call failed');
		return { isValid: true };
	}
};
